// typeDisplayPolicy.js — where a type appears in Menu and + Add

import { isSystemTypeData } from 'security/typeAccess';
import { SKETCH_TYPE_KEY } from 'utilities/sketchType';
import { typeAllowsTopLevelCreate } from 'utilities/typeCreatePolicy';

/**
 * Types that stay out of the generic + Add list unless `showInAddMenu` is set.
 * Capture types (Note / Sketch) are pinned separately. Media becomes Take photo /
 * Upload. Infrastructure types are never offered as “new record”.
 * EVENT is intentionally omitted — it is a normal add-menu type by default.
 */
export const DEFAULT_HIDDEN_ADD_MENU_TYPE_KEYS = Object.freeze(new Set([
  'NODE',
  'USER',
  'LIVE_ROOM',
  'LIVE_STREAM',
  'MEETING',
  'NOTE',
  SKETCH_TYPE_KEY,
  'COMMENT',
  'PHOTO',
  'VIDEO',
  'DOCUMENT',
  'FILE',
  'SNAPSHOT',
  'RECORDING',
  'CLICKUP_TASK',
]));

/**
 * resolveTypeKey
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {string} Uppercase type key, or empty string.
 */
function resolveTypeKey(typeDefOrKey) {
  return String(
    (typeof typeDefOrKey === 'string' ? typeDefOrKey : typeDefOrKey?.key)
    || '',
  ).trim().toUpperCase();
}

/**
 * resolveTypeDef
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {object|null}
 */
function resolveTypeDef(typeDefOrKey) {
  return typeof typeDefOrKey === 'object' && typeDefOrKey
    ? typeDefOrKey
    : null;
}

/**
 * typeShowsInMenu
 *
 * Whether this type gets a left-nav Menu row (Media types with this on go to
 * Media instead). Independent of create policy.
 *
 * Missing `showInMenu` keeps today’s behavior: system + top-level create.
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {boolean}
 */
export function typeShowsInMenu(typeDefOrKey) {
  const typeDef = resolveTypeDef(typeDefOrKey);

  // Until Appear-as is stored, PROJECT uses Lead / Project rows only.
  if (resolveTypeKey(typeDefOrKey) === 'PROJECT' && !Array.isArray(typeDef?.appearAs)) {
    return false;
  }

  if (typeDef && Object.prototype.hasOwnProperty.call(typeDef, 'showInMenu')) {
    return typeDef.showInMenu !== false;
  }

  if (!typeDef) return false;
  return isSystemTypeData(typeDef) && typeAllowsTopLevelCreate(typeDef);
}

/**
 * typeShowsInAddMenu
 *
 * Whether this type appears as a generic row in + Add. Independent of
 * Allow Top-Level Create (that flag still gates space-root create).
 *
 * Missing `showInAddMenu` hides the hardcoded capture / media / internal types.
 *
 * @param {object|string|null|undefined} typeDefOrKey - Type def or type key.
 * @returns {boolean}
 */
export function typeShowsInAddMenu(typeDefOrKey) {
  const typeDef = resolveTypeDef(typeDefOrKey);

  if (resolveTypeKey(typeDefOrKey) === 'PROJECT' && !Array.isArray(typeDef?.appearAs)) {
    return false;
  }

  if (typeDef && Object.prototype.hasOwnProperty.call(typeDef, 'showInAddMenu')) {
    return typeDef.showInAddMenu !== false;
  }

  const typeKey = resolveTypeKey(typeDefOrKey);
  if (!typeKey) return true;
  return !DEFAULT_HIDDEN_ADD_MENU_TYPE_KEYS.has(typeKey);
}
